import { CommonModule } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { LetDirective } from '@ngrx/component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DataleanBaseApiService, ListComponent, MessageHandler, PaginationInfo, SearchComponent, TableRow } from 'addiction-components';
import { LottieModule } from 'ngx-lottie';
import { environment } from 'src/environments/environment';
import { ApplicationUserGroup, EntityType, EntityViewPermission, SubjectType, isAppUser, isGroup } from '../../models';
import { ViewPermissionService } from '../../services/view-permission.service';
import { EntitySelectorDialogInfinityComponent } from '../entity-selector-dialog-infinity/entity-selector-dialog-infinity.component';

@Component({
	selector: 'datalean-group-selector-permission-dialog',
	templateUrl: './group-selector-permission-dialog.component.html',
	styleUrls: ['./group-selector-permission-dialog.component.scss'],
	standalone: true,
	imports: [CommonModule, ListComponent, TranslateModule, SearchComponent, MatTabsModule, LetDirective, MatCheckboxModule, LottieModule],
})
export class GroupSelectorPermissionDialogComponent extends EntitySelectorDialogInfinityComponent<
	ApplicationUserGroup,
	{ selected: EntityViewPermission[] }
> {
	private viewPermissionService = inject(ViewPermissionService);
	private translateService = inject(TranslateService);
	private messageHandler = inject(MessageHandler);
	private baseApi = inject(DataleanBaseApiService);

	private savedViewPermission: EntityViewPermission[] = [];

	readonly env = environment;

	@Input() override searchFields: string = 'name';
	@Input() override searchParamName: string = 'q';
	@Input() override fieldsToShow: string[] = ['name'];
	@Input() entityUUID: string = '';
	@Input() entityType: EntityType = EntityType.FEATURE_VALUE;
	//settare a true se serve che oltre a application group e smart group sempre presenti
	//vengano visualizzati anche gli utenti applicativi
	@Input() showUserApplication?: boolean = false;
	@Input() assignPermission?: boolean = true;

	constructor() {
		super();
	}

	override mapData(data: ApplicationUserGroup[]): TableRow[] {
		const result: TableRow[] = [];

		for (const item of data) {
			if (isAppUser(item)) {
				result.push({
					uuid: item.uuid,
					name: item.firstName + ' ' + item.lastName,
				});
			} else if (isGroup(item)) {
				result.push({
					uuid: item.uuid,
					name: `${item.name}`,
				});
			}
		}

		// console.log('result', result);

		return result;
	}

	onPageChanged(pages: number[]) {
		this.currentPage$.next(pages.map((p) => new PaginationInfo(this.pageSize, p)));
	}

	override close() {
		// console.log('close ', this.savedViewPermission);
		this.closeDialog({ selected: this.savedViewPermission });
	}

	onSelectionChange(entityUUID?: string, checked?: boolean): void {
		if (entityUUID) {
			if (entityUUID === 'all') {
				for (const uuid of this._currentPageUUIDs) {
					this.gcs.checkboxMap[uuid] = !!checked;
				}
				this.indeterminateUUIDs = [];
			} else {
				if (this.indeterminateUUIDs?.includes(entityUUID)) {
					this.indeterminateUUIDs = this.indeterminateUUIDs.filter((uuid) => uuid !== entityUUID);
				}
				// this.gcs.reset();
				this.gcs.checkboxMap[entityUUID] = !!checked;
				if (!checked) {
					if (this.gcs.selectAllChecked) {
						for (const uuid of this._currentPageUUIDs.filter((uuid) => uuid !== entityUUID)) {
							this.gcs.checkboxMap[uuid] = true;
						}
					}
					this.gcs.selectAllChecked = false;
				}
				this.gcs.updateSelectAllStatus();
			}
		}

		this.selectionChanged(
			Object.entries(this.gcs.checkboxMap).reduce((acc, [uuid, value]) => {
				if (value) {
					acc.push({ uuid });
				}
				return acc;
			}, new Array<TableRow>())
		);

		this.assignPermissionViews(this.entityUUID);
	}

	assignPermissionViews(entityUUID: string) {
		let subjectType: SubjectType;
		switch (this.endpoint) {
			case environment.groupsUrl:
				subjectType = SubjectType.APPLICATION_GROUP;
				break;
			case environment.smartGroupsUrl:
				subjectType = SubjectType.APPLICATION_SMART_GROUP;
				break;
			case environment.applicationUsersUrl:
				subjectType = SubjectType.APPLICATION_USER;
				break;
		}

		// console.log(this.gcs.checkboxMap);

		const viewPermissions = Object.entries(this.gcs.checkboxMap)
			.filter(([uuid]) => this.assignPermission || this._currentPageUUIDs.includes(uuid))
			.reduce((acc, [uuid, value]) => {
				acc.push(this.viewPermissionService.createPermissionViewPermission(value, this.entityType, entityUUID, subjectType, uuid));
				// console.log('acc', acc);
				return acc;
			}, new Array<EntityViewPermission>())
			.filter((vp) => vp.canView !== this.negativeReflection || (!this.assignPermission && !this.indeterminateUUIDs?.includes(vp.subjectUUID)));

		if (this.assignPermission) {
			this.baseApi.createOne(this.getUrl(entityUUID), viewPermissions || []).subscribe({
				next: () => {
					this.messageHandler.createToast(this.translateService.instant('VIEW_PERMISSIONS.UPDATE_SUCCESS'));
				},
				error: () => {
					this.messageHandler.createErrorToast(this.translateService.instant('VIEW_PERMISSIONS.ERRORS.UPDATE_FAIL'));
				},
			});
		} else {
			this.savedViewPermission = this.savedViewPermission.filter((vp) => !viewPermissions.some((vp2) => vp2.subjectUUID === vp.subjectUUID));
			this.savedViewPermission = [...this.savedViewPermission, ...viewPermissions];
		}
	}

	getUrl(entityUUID: string) {
		switch (this.entityType) {
			case EntityType.FEATURE_VALUE:
				return `${environment.featureValueUrl.replace('v2/', '')}${entityUUID}/permission`;
			case EntityType.ASSET:
				return `${environment.mediaLibraryUrl}${entityUUID}/permission`;
			case EntityType.ALBUM:
				return `${environment.collectionsUrl}${entityUUID}/permission`;
			case EntityType.PRODUCT:
				return `${environment.productUrl}${entityUUID}/permission`;
    	case EntityType.STRUCTURE:
				return `${environment.structureUrl}${entityUUID}/permission`;
      case EntityType.STRUCTURE_FIELD:
				return `${environment.structureUrl.replace('structure', 'structureField')}${entityUUID}/permission`;
			default:
				return `${environment.featureValueUrl.replace('v2/', '')}${entityUUID}/permission`;
		}
	}

	selectedTabChange(event: MatTabChangeEvent) {
		this.searchFields = 'name';
		switch (event.index) {
			case 0:
				this.endpoint = environment.groupsUrl;
				break;
			case 1:
				this.endpoint = environment.smartGroupsUrl;
				break;
			case 2:
				this.endpoint = environment.applicationUsersUrl;
				this.searchFields = 'firstName,lastName';
				break;
		}
		this.selectedUUIDs = [];
		this.switchTab$.next(undefined);
	}
}
